import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { z } from 'zod';
import type { AccountStatus, AppRole } from '@exigo/shared';
import { api, ApiError, BASE_URL, setCsrfToken } from '../api/client';
import { SessionResponse } from '../api/types';

/**
 * Auth is a server-brokered OAuth flow (security.md §3): the browser never
 * talks to Entra or holds a token. `signIn`/silent-SSO are full top-level
 * navigations to the API's own /auth/microsoft, which redirects to Entra (or,
 * when ENABLE_DEV_LOGIN is set server-side, straight to /auth/dev-login) —
 * there is no client-side "dev mode" branch left to choose between, unlike
 * the MSAL-browser era this replaces. Identity comes from a single source,
 * GET /auth/session, which also hands back the CSRF token every write needs.
 */

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

/**
 * Keeps the field names the rest of the app already reads (Sidebar,
 * DashboardPage, ScenarioListPage, RequireRole) — `roles` stays a one-element
 * array specifically so RequireRole's `user.roles.includes(...)` needs no
 * change, even though the server only ever has one role per user now.
 */
export interface AuthUser {
  displayName: string;
  upn: string;
  roles: AppRole[];
  /**
   * Whether an admin has granted this account access. Anything other than
   * `active` and the app renders the awaiting-approval screen instead of the
   * router — every API route but this one would refuse anyway. See App.tsx.
   */
  status: AccountStatus;
}

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  errorMessage: string | null;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Sign-in failures are reported as a fixed set of codes on the redirect back
 * from /auth/microsoft/callback — mapping them here means the server never
 * has to echo IdP or database error text into a page, which is both an
 * information leak and an injection surface.
 */
const ERROR_MESSAGES: Record<string, string> = {
  sso_not_configured:
    'Single sign-on is not configured on this environment. Contact your administrator.',
  sso_denied: 'Microsoft sign-in was cancelled or denied.',
  invalid_callback: 'The sign-in link has expired. Please try again.',
  state_mismatch: 'The sign-in request could not be verified. Please try again.',
  token_exchange_failed: 'Microsoft could not complete the sign-in. Please try again.',
  idp_unreachable: 'Microsoft sign-in is temporarily unreachable. Please try again shortly.',
  invalid_id_token: 'The sign-in response could not be verified. Please try again.',
  no_email_claim: 'Your Microsoft account did not return an email address.',
  domain_not_allowed: "This account's email domain is not permitted to access this application.",
  account_provisioning_failed:
    'Your account could not be provisioned. Please contact the helpdesk.',
};

const LogoutResponse = z.object({ success: z.boolean() }).strict();

function currentReturnTo(): string {
  return window.location.pathname + window.location.search;
}

/** Strips the one-shot query flags so a refresh or a shared link never replays them. */
function clearAuthQueryParams(): void {
  const url = new URL(window.location.href);
  if (
    !url.searchParams.has('signed_in') &&
    !url.searchParams.has('sso') &&
    !url.searchParams.has('error')
  ) {
    return;
  }
  url.searchParams.delete('signed_in');
  url.searchParams.delete('sso');
  url.searchParams.delete('error');
  window.history.replaceState(null, '', `${url.pathname}${url.search}`);
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // An object property, not a plain `let`: see AuthProvider's prior
    // revision — TypeScript narrows a captured `let` to its last-known value
    // across this async closure and would treat every later `if
    // (state.cancelled)` as dead code. A property read is never narrowed
    // that way.
    const state = { cancelled: false };

    // A failed callback comes back with ?error=<code>; map it to something a
    // person can act on, then strip it so a refresh does not replay it.
    const errorCode = new URLSearchParams(window.location.search).get('error');
    if (errorCode)
      setErrorMessage(ERROR_MESSAGES[errorCode] ?? 'Sign-in failed. Please try again.');
    clearAuthQueryParams();

    async function boot(): Promise<void> {
      try {
        const session = await api.get('/auth/session', SessionResponse);
        if (state.cancelled) return;
        setCsrfToken(session.csrfToken);
        setUser({
          displayName: session.user.name,
          upn: session.user.email,
          roles: [session.user.role],
          status: session.user.status,
        });
        setStatus('authenticated');
        return;
      } catch (err) {
        if (state.cancelled) return;
        // Anything other than "not signed in" (e.g. the API is down) is a real
        // error worth showing, not a reason to render the sign-in button.
        if (!(err instanceof ApiError && err.isUnauthenticated)) {
          setStatus('error');
          setErrorMessage(err instanceof Error ? err.message : 'Could not reach the API.');
          return;
        }
      }

      // No session: show the sign-in screen and let the person click.
      //
      // There is deliberately NO automatic `prompt=none` attempt here. It is
      // tempting — it would land anyone with an existing tenant session
      // straight on the dashboard — but when the browser has no Entra session
      // at all, Entra does not redirect back with `error=login_required` as
      // the flow assumes. It renders its own AADSTS50058 error page and stops
      // dead, stranding a first-time visitor on a Microsoft error screen with
      // no route back into the app. Verified against the real tenant.
      //
      // The usual way to make a silent attempt safe is to run it in a hidden
      // iframe so a dead-end error page renders out of sight, but that needs
      // `frame-src https://login.microsoftonline.com`, and dropping that from
      // the CSP was one of the wins of moving to server-brokered auth
      // (security.md §5 — never weaken the CSP to make a flow work).
      //
      // The server still handles `prompt=none` correctly for the case where a
      // session exists but needs interaction, so this can be revisited if a
      // safe trigger appears; it just must not fire unprompted on load.
      setStatus('unauthenticated');
    }

    boot().catch((err: unknown) => {
      if (state.cancelled) return;
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Sign-in failed.');
    });

    return () => {
      state.cancelled = true;
    };
  }, []);

  const signIn = useCallback(() => {
    // A full navigation, built from BASE_URL rather than a bare path — see
    // the comment on BASE_URL in api/client.ts for why that distinction is
    // load-bearing on a two-subdomain deployment.
    window.location.assign(
      `${BASE_URL}/auth/microsoft?return_to=${encodeURIComponent(currentReturnTo())}`,
    );
  }, []);

  const signOut = useCallback(() => {
    api
      .post('/auth/logout', LogoutResponse)
      .catch(() => undefined)
      .finally(() => {
        setCsrfToken(null);
        setUser(null);
        setStatus('unauthenticated');
      });
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, user, errorMessage, signIn, signOut }),
    [status, user, errorMessage, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
